import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FiCalendar,
  FiExternalLink,
  FiMapPin,
  FiMaximize2,
  FiX,
} from "react-icons/fi";
import { FaPause, FaPlay, FaVolumeUp } from "react-icons/fa";

import {
  FALLBACK_EVENT_IMAGE,
  getEventStatusLabel,
} from "../utils/eventsUtils";

const stripHtml = (html = "") =>
  String(html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

function AudioReadButton({ title, subtitle, dateLabel, location, description }) {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const canSpeak =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  useEffect(() => {
    return () => {
      if (canSpeak) {
        window.speechSynthesis.cancel();
      }
    };
  }, [canSpeak]);

  const handleRead = () => {
    if (!canSpeak) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const textToRead = [
      title,
      subtitle,
      dateLabel ? `Date : ${dateLabel}` : "",
      location ? `Lieu : ${location}` : "",
      description,
    ]
      .filter(Boolean)
      .join(". ");

    if (!textToRead.trim()) return;

    const utterance = new window.SpeechSynthesisUtterance(textToRead);
    utterance.lang = "fr-FR";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.cancel();
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  if (!canSpeak) return null;

  return (
    <button
      type="button"
      onClick={handleRead}
      aria-label={
        isSpeaking
          ? "Arrêter la lecture audio de l’événement"
          : "Lire l’événement en audio"
      }
      title={
        isSpeaking
          ? "Arrêter la lecture audio de l’événement"
          : "Lire l’événement en audio"
      }
      className="group inline-flex min-h-10 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-[#1277B9]/30 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-300"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1277B9]/10 text-[#1277B9] transition group-hover:bg-[#1277B9] group-hover:text-white dark:bg-sky-500/15 dark:text-sky-300">
        {isSpeaking ? (
          <FaPause className="h-3 w-3" />
        ) : (
          <FaPlay className="h-3 w-3 translate-x-[1px]" />
        )}
      </span>

      <span>{isSpeaking ? "Arrêter" : "Écouter"}</span>

      <FaVolumeUp className="hidden h-3.5 w-3.5 opacity-60 sm:block" />
    </button>
  );
}

export default function EventDetailModal({ event, onClose }) {
  const [zoomImage, setZoomImage] = useState(false);

  const safeHtml = useMemo(() => {
    return DOMPurify.sanitize(event?.descriptionRaw || "");
  }, [event]);

  const readableDescription = useMemo(() => {
    if (event?.descriptionText) return event.descriptionText;
    return stripHtml(event?.descriptionRaw || "");
  }, [event]);

  useEffect(() => {
    const onKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key !== "Escape") return;

      if (zoomImage) {
        setZoomImage(false);
        return;
      }

      onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, zoomImage]);

  useEffect(() => {
    return () => {
      if (
        typeof window !== "undefined" &&
        "speechSynthesis" in window &&
        window.speechSynthesis.speaking
      ) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  if (!event) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:px-4"
        onClick={onClose}
      >
        <div
          className="relative flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-950"
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-950/70 text-white transition hover:bg-slate-950 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="Fermer"
          >
            <FiX className="h-5 w-5" />
          </button>

          <div className="relative bg-slate-950">
            <img
              src={event.image}
              alt={event.title}
              className="max-h-[42vh] w-full object-cover"
              onError={(imageEvent) => {
                imageEvent.currentTarget.src = FALLBACK_EVENT_IMAGE;
              }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />

            <div className="absolute bottom-4 left-4 right-16">
              <div className="flex flex-wrap items-center gap-2">
                {event.category && (
                  <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#1277B9] shadow-sm backdrop-blur">
                    {event.category}
                  </span>
                )}

                <span className="inline-flex rounded-full bg-[#1277B9] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                  {getEventStatusLabel(event.status)}
                </span>
              </div>

              <h2 className="mt-3 text-2xl font-bold leading-tight text-white md:text-3xl">
                {event.title}
              </h2>

              {event.subtitle && (
                <p className="mt-2 max-w-3xl text-sm font-semibold text-white/80">
                  {event.subtitle}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setZoomImage(true)}
              className="absolute left-4 top-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950/70 px-4 text-sm font-bold text-white transition hover:bg-slate-950"
            >
              <FiMaximize2 className="h-4 w-4" />
              Agrandir
            </button>
          </div>

          <div className="overflow-y-auto px-5 py-5 sm:px-7">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="grid flex-1 gap-3 md:grid-cols-2">
                {event.dateLabel && (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                    <div className="flex items-start gap-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                        <FiCalendar className="h-4 w-4" />
                      </span>
                      <span className="pt-2">{event.dateLabel}</span>
                    </div>
                  </div>
                )}

                {event.location && (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                    <div className="flex items-start gap-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                        <FiMapPin className="h-4 w-4" />
                      </span>
                      <span className="pt-2">{event.location}</span>
                    </div>
                  </div>
                )}
              </div>

              <AudioReadButton
                title={event.title}
                subtitle={event.subtitle}
                dateLabel={event.dateLabel}
                location={event.location}
                description={readableDescription}
              />
            </div>

            {safeHtml ? (
              <div
                className="prose prose-sm mt-6 max-w-none leading-relaxed text-slate-700 prose-headings:text-slate-950 prose-a:text-[#1277B9] prose-img:rounded-2xl dark:prose-invert dark:text-slate-200 sm:prose-base"
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            ) : event.descriptionText ? (
              <p className="mt-6 text-sm leading-7 text-slate-700 dark:text-slate-200 sm:text-base">
                {event.descriptionText}
              </p>
            ) : null}

            {event.externalLink && (
              <div className="mt-6">
                <a
                  href={event.externalLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d]"
                >
                  Plus d’informations
                  <FiExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {zoomImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          onClick={() => setZoomImage(false)}
        >
          <button
            type="button"
            onClick={() => setZoomImage(false)}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Fermer l’image"
          >
            <FiX className="h-5 w-5" />
          </button>

          <img
            src={event.image}
            alt={event.title}
            className="max-h-[92vh] max-w-[95vw] object-contain"
            onError={(imageEvent) => {
              imageEvent.currentTarget.src = FALLBACK_EVENT_IMAGE;
            }}
          />
        </div>
      )}
    </>
  );
}